import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Zap, ArrowLeft, ShieldCheck, AlertCircle, KeyRound } from 'lucide-react';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!token) { setErrorMsg('Token mancante nel link. Richiedi un nuovo ripristino.'); return; }
    if (pwd.length < 6) { setErrorMsg('La password deve avere almeno 6 caratteri.'); return; }
    if (pwd !== pwd2) { setErrorMsg('Le due password non coincidono.'); return; }
    setSubmitting(true);
    try {
      await api.resetPassword(token, pwd);
      setDone(true);
      toast.success('Password aggiornata. Ora puoi accedere.');
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorMsg(detail || 'Impossibile ripristinare la password. Il link potrebbe essere scaduto o già utilizzato.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <div className="size-9 rounded-xl bg-primary text-primary-foreground grid place-items-center"><Zap className="size-5" /></div>
          <div className="font-heading text-lg font-semibold">CRM Utility Boost</div>
        </div>
        <h2 className="font-heading text-2xl font-semibold mb-1 flex items-center gap-2"><KeyRound className="size-5 text-primary" />Imposta nuova password</h2>
        <p className="text-sm text-muted-foreground mb-6">Scegli una password di almeno 6 caratteri. Al termine ti riporteremo alla schermata di login.</p>

        <Card>
          <CardContent className="p-5">
            {done ? (
              <Alert data-testid="reset-success-alert">
                <ShieldCheck className="size-4 text-primary" />
                <AlertTitle>Password aggiornata</AlertTitle>
                <AlertDescription>Ti stiamo reindirizzando al login...</AlertDescription>
              </Alert>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {errorMsg && (
                  <Alert variant="destructive" data-testid="reset-error-alert">
                    <AlertCircle className="size-4" />
                    <AlertTitle>Errore</AlertTitle>
                    <AlertDescription>{errorMsg}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="pwd1">Nuova password</Label>
                  <Input id="pwd1" type="password" value={pwd} onChange={(e) => { setPwd(e.target.value); if (errorMsg) setErrorMsg(''); }} autoComplete="new-password" required data-testid="reset-pwd-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pwd2">Ripeti nuova password</Label>
                  <Input id="pwd2" type="password" value={pwd2} onChange={(e) => { setPwd2(e.target.value); if (errorMsg) setErrorMsg(''); }} autoComplete="new-password" required data-testid="reset-pwd2-input" />
                </div>
                <Button type="submit" className="w-full" disabled={submitting} data-testid="reset-submit-button">
                  {submitting ? 'Aggiornamento...' : 'Imposta nuova password'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <div className="text-sm text-center mt-6">
          <Link to="/login" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="reset-back-to-login">
            <ArrowLeft className="size-3.5" />Torna al login
          </Link>
        </div>
      </div>
    </div>
  );
}
